import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, IconButton, Box, Tooltip, Chip, Avatar, Badge, Menu, MenuItem,
  ListItemIcon, ListItemText, Divider, useTheme, useMediaQuery,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import TvIcon from '@mui/icons-material/Tv';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

import { AuthContext, MyContext } from '../context/AuthContext';
import { NotificationsContext } from '../context/NotificationsContext';
import { ColorModeContext } from '../context/ColorModeContext';
import { supabase } from '../supabaseClient';
import { getSoundsEnabled, setSoundsEnabled } from '../lib/sounds';
import TimeWalletIcon from '../resource/TimeWalletIcon';
import WLogoAnimated from '../resource/WLogoAnimated';
import WipeHouseRedux from '../resource/WipeHouseRedux';
import SearchBar from '../Search/SearchBar';
import SearchSheet from './SearchSheet';
import WipePlusBadge from '../components/WipePlusBadge';

/**
 * TOPBAR — fixed-height (56px on mobile, 64px on desktop) top bar.
 *
 * Replaces the old `Nav.tsx` AppBar that had a dual-row layout on
 * mobile. Search becomes a button-into-sheet on mobile, inline on
 * desktop. The hamburger button opens a Drawer on mobile/tablet only;
 * on desktop it doesn't render because the SideNav lives in the grid.
 *
 * The component is dumb about layout — it only knows what to render
 * inside its 56px row. The grid surrounding it is `AppShell`.
 */

interface TopBarProps {
  onOpenDrawer: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onOpenDrawer }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // The 600-899px range still has tight horizontal space — use the
  // compact logo + abbreviated wallet so the topbar doesn't clip.
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();

  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const user = useContext<MyContext>(AuthContext).user;
  const { notifications } = useContext(NotificationsContext);
  const { darkMode, toggleColorMode } = useContext(ColorModeContext);

  // ── Wallet countdown — exact same logic as the legacy NavBar ──────────
  const [walletSeconds, setWalletSeconds] = useState<number>(0);
  const lastSavedRef = useRef<number>(0);

  useEffect(() => {
    if (!userRecord) return;
    setWalletSeconds(userRecord.time_wallet_seconds);
    lastSavedRef.current = userRecord.time_wallet_seconds;
  }, [userRecord?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userRecord) return;
    setWalletSeconds((prev) => {
      const diff = Math.abs(userRecord.time_wallet_seconds - prev);
      if (diff > 300) {
        lastSavedRef.current = userRecord.time_wallet_seconds;
        return userRecord.time_wallet_seconds;
      }
      return prev;
    });
  }, [userRecord?.time_wallet_seconds]);

  useEffect(() => {
    if (!userRecord) return;
    const interval = setInterval(() => {
      setWalletSeconds((prev) => {
        const next = prev - 1;
        if (lastSavedRef.current - next >= 60) {
          lastSavedRef.current = next;
          supabase.from('users').update({ time_wallet_seconds: next }).eq('id', userRecord.id);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [userRecord?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const WALLET_FULL = 31_536_000;
  const walletPct = Math.max(0, Math.min(100, (walletSeconds / WALLET_FULL) * 100));

  // Death-model awareness: how long until the next monthly wipe? If your
  // wallet won't survive until then, you're at risk of dying. If you're
  // ALREADY dying (dying_at set), the topbar shows purgatory state.
  const secondsToWipe = (() => {
    const now = new Date();
    const nw = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
    return Math.max(0, Math.floor((nw.getTime() - Date.now()) / 1000));
  })();
  const isDying = !!userRecord?.dying_at;
  const isAtRisk = !isDying && !!userRecord && walletSeconds <= secondsToWipe && userRecord.permanence_tokens === 0;
  const purgatoryRemainingSec = (() => {
    if (!userRecord?.dying_at) return 0;
    const dyingMs = new Date(userRecord.dying_at).getTime();
    const reaperMs = dyingMs + 7 * 86400 * 1000;
    return Math.max(0, Math.floor((reaperMs - Date.now()) / 1000));
  })();

  const walletRingColor = (() => {
    if (isDying) return '#0b0f0b'; // purgatory: dead-black ring
    if (isAtRisk) return '#a02018'; // deathRed token
    if (walletSeconds < 3600) return '#b51e1b';
    if (walletSeconds < 86400) return '#ff9800';
    const t = walletPct / 100;
    const r = Math.round(181 * (1 - t) + 22 * t);
    const g = Math.round(30 * (1 - t) + 168 * t);
    return `rgb(${r},${g},27)`;
  })();

  const fmtDur = (s: number) => {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };
  const walletTooltip = isDying
    ? `DYING — confirmed death in ${fmtDur(purgatoryRemainingSec)} unless revived. See /graveyard.`
    : isAtRisk
      ? `ACCOUNT DEATH IN ${fmtDur(secondsToWipe)} — your wallet won't survive the next wipe. Post, vote, or visit /store.`
      : 'Your time wallet — ticks down 1s per second while browsing.';

  const walletShortLabel = (() => {
    const s = Math.max(0, walletSeconds);
    const pad = (n: number) => String(n).padStart(2, '0');
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (isCompact) {
      // On mobile + small tablet, prefer the shortest possible label so the
      // chip doesn't push the avatar/menu off-screen. Drop seconds and use
      // letter suffixes (`d`/`h`/`m`) instead of `:` digits which read
      // as longer in monospace VT323.
      if (s < 60) return `${sec}s`;
      if (s < 3600) return `${m}m`;
      if (d === 0) return `${h}h ${m}m`;
      if (d < 10) return `${d}d ${h}h`;
      return `${d}d`;
    }
    const hms = `${pad(h)}:${pad(m)}:${pad(sec)}`;
    if (s < 3600) return `${pad(m)}:${pad(sec)}`;
    if (d === 0) return hms;
    return `${d}d ${hms}`;
  })();

  // ── Document title countdown ──────────────────────────────────────────
  useEffect(() => {
    const nextWipeSeconds = () => {
      const now = new Date();
      const nw = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
      return Math.max(0, Math.floor((nw.getTime() - Date.now()) / 1000));
    };
    const update = () => {
      const s = nextWipeSeconds();
      const d = Math.floor(s / 86400);
      const h = Math.floor((s % 86400) / 3600);
      document.title = d > 0 ? `Wipe — ${d}d ${h}h left` : `Wipe — ${h}h left`;
    };
    update();
    const id = setInterval(update, 60_000);
    return () => { clearInterval(id); document.title = 'Wipe'; };
  }, []);

  // ── User menu state ───────────────────────────────────────────────────
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const handleUserMenuOpen = (e: React.MouseEvent<HTMLElement>) => setUserMenuAnchor(e.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleSignOut = async () => {
    handleUserMenuClose();
    await supabase.auth.signOut();
    navigate('/');
  };

  // ── Sounds toggle ─────────────────────────────────────────────────────
  const [soundsOn, setSoundsOn] = useState(getSoundsEnabled());
  const toggleSounds = () => {
    const next = !soundsOn;
    setSoundsEnabled(next);
    setSoundsOn(next);
  };

  // ── CRT toggle ────────────────────────────────────────────────────────
  const [crtOn, setCrtOn] = useState<boolean>(() => {
    try { return localStorage.getItem('wipe_crt') === '1'; } catch { return false; }
  });
  const toggleCRT = () => {
    const next = !crtOn;
    try { localStorage.setItem('wipe_crt', next ? '1' : '0'); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('wipe-crt-toggle', { detail: { on: next } }));
    setCrtOn(next);
  };

  // ── Search sheet (mobile) ─────────────────────────────────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <>
      <AppBar
        position="static"
        color="inherit"
        elevation={0}
        sx={{
          boxShadow: '0 1px 0 0 ' + theme.palette.divider,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: '56px !important', sm: '64px !important' },
            height: { xs: 56, sm: 64 },
            px: { xs: 1, sm: 2 },
            gap: { xs: 0.25, sm: 1 },
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
          }}
        >
          {/* LEFT: hamburger (only when SideNav is hidden) + logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1, gap: { xs: 0.25, sm: 1 } }}>
            {!isDesktop && (
              <IconButton
                onClick={onOpenDrawer}
                edge="start"
                size="small"
                sx={{ flexShrink: 0, p: 0.5 }}
                aria-label="open navigation drawer"
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            )}
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                color: 'inherit',
                flexShrink: 0,           // don't let the brand shrink
                overflow: 'visible',     // never clip the logo glyph
                minHeight: 36,           // give the SVG breathing room inside the 56/64px row
              }}
              aria-label="home"
            >
              {isCompact ? <WLogoAnimated /> : <WipeHouseRedux className="font-face-nokia" />}
            </Link>
          </Box>

          {/* MIDDLE: search bar — inline on desktop only (≥ 900px), search-icon
              opens fullscreen sheet on mobile + tablet (< 900px). The old
              breakpoint at 600px crowded iPad Mini and clipped the brand. */}
          {!isCompact ? (
            <Box sx={{ flex: 1, mx: 2, maxWidth: 600, minWidth: 0 }} ref={searchRef}>
              <SearchBar parentRef={searchRef} />
            </Box>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}

          {/* RIGHT: search trigger (mobile) + wallet + theme + notifs + avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.75 }, flexShrink: 0 }}>
            {isCompact && (
              <Tooltip title="Search">
                <IconButton size="small" onClick={() => setSearchOpen(true)} sx={{ p: 0.75 }} aria-label="Open search">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {userRecord && (
              <Tooltip title={walletTooltip} arrow>
                <Chip
                  icon={
                    <Box
                      className="wallet-ring"
                      sx={{
                        width: 24,
                        height: 24,
                        ml: 0.5,
                        flexShrink: 0,
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgressbarWithChildren
                        value={walletPct}
                        styles={buildStyles({
                          pathColor: walletRingColor,
                          trailColor: 'rgba(128,128,128,0.2)',
                          pathTransitionDuration: 1,
                        })}
                      >
                        <Box className="wallet-ring__icon" sx={{ display: 'flex' }}>
                          <TimeWalletIcon />
                        </Box>
                      </CircularProgressbarWithChildren>
                    </Box>
                  }
                  label={isDying ? `DYING ${fmtDur(purgatoryRemainingSec)}` : walletShortLabel}
                  variant="outlined"
                  size="small"
                  component={isDying ? Link : 'div'}
                  to={isDying ? '/graveyard' : undefined}
                  clickable={isDying}
                  className={(isDying || walletSeconds < 3600 || isAtRisk) ? 'wallet-critical' : undefined}
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    fontFamily: '"VT323", monospace',
                    fontSize: { xs: '0.72rem', sm: '1.05rem' },
                    letterSpacing: 0,
                    borderColor: walletRingColor,
                    color: walletRingColor,
                    flexShrink: 1,
                    minWidth: 0,
                    // Cap mobile width so a long label can never push the
                    // search/avatar off-screen. The label internally
                    // truncates with ellipsis if it overflows.
                    maxWidth: { xs: 110, sm: 'none' },
                    height: { xs: 26, sm: 32 },
                    '& .MuiChip-label': {
                      px: { xs: 0.5, sm: 1 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    '& .MuiChip-icon': { color: 'inherit', mr: 0 },
                  }}
                />
              </Tooltip>
            )}

            {/* Theme toggle — desktop+tablet only */}
            <Tooltip title={darkMode ? 'Light mode' : 'Dark mode'}>
              <IconButton
                onClick={toggleColorMode}
                size="small"
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* Notifications — desktop+tablet only (mobile uses tab bar) */}
            {userRecord && (
              <IconButton
                size="small"
                component={Link}
                to="/notifications"
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}
                aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </IconButton>
            )}

            {/* User avatar / menu — now visible on mobile too so users
                have a path to sign out, toggle theme, and reach the
                Sounds/CRT toggles. Previously hidden on xs which made
                those items completely unreachable (launch blocker —
                UI audit C1 + C3). */}
            {userRecord ? (
              <>
                <Tooltip title={`@${userRecord.username}`}>
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={handleUserMenuOpen}
                    sx={{ flexShrink: 0, p: 0.5 }}
                    aria-label={`Open user menu for @${userRecord.username}`}
                  >
                    <Avatar
                      src={userRecord.avatar_url ?? undefined}
                      sx={{ width: { xs: 26, sm: 30 }, height: { xs: 26, sm: 30 }, bgcolor: 'primary.main', fontSize: '0.85rem' }}
                    >
                      {userRecord.username.charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  PaperProps={{ sx: { borderRadius: 2, minWidth: 220 } }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem component={Link} to={`/${userRecord.username}`} onClick={handleUserMenuClose} dense>
                    <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Profile
                          <WipePlusBadge user={userRecord} linkToStore={false} />
                        </Box>
                      }
                      secondary={`@${userRecord.username}`}
                      secondaryTypographyProps={{ sx: { fontSize: '0.7rem' } }}
                    />
                  </MenuItem>
                  <MenuItem component={Link} to="/store" onClick={handleUserMenuClose} dense>
                    <ListItemIcon><StorefrontIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Store" />
                  </MenuItem>
                  <MenuItem component={Link} to="/invite" onClick={handleUserMenuClose} dense>
                    <ListItemIcon><CardGiftcardIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Invite friends" />
                  </MenuItem>
                  <MenuItem component={Link} to="/settings" onClick={handleUserMenuClose} dense>
                    <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Settings" />
                  </MenuItem>
                  <Divider />
                  {isMobile && (
                    <MenuItem onClick={() => { toggleColorMode(); handleUserMenuClose(); }} dense>
                      <ListItemIcon>
                        {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText primary={darkMode ? 'Light mode' : 'Dark mode'} />
                    </MenuItem>
                  )}
                  <MenuItem onClick={toggleSounds} dense>
                    <ListItemIcon>
                      {soundsOn ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={soundsOn ? 'Sounds: on' : 'Sounds: off'}
                      secondary="Vote/post audio cues"
                      secondaryTypographyProps={{ sx: { fontSize: '0.7rem' } }}
                    />
                  </MenuItem>
                  <MenuItem onClick={toggleCRT} dense>
                    <ListItemIcon><TvIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary={crtOn ? 'CRT mode: on' : 'CRT mode: off'}
                      secondary="Retro scanline overlay"
                      secondaryTypographyProps={{ sx: { fontSize: '0.7rem' } }}
                    />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleSignOut} dense>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Sign out" />
                  </MenuItem>
                </Menu>
              </>
            ) : (
              !user && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" component={Link} to="/login" aria-label="Sign in">
                    <PersonIcon fontSize="small" />
                  </IconButton>
                </Box>
              )
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile fullscreen search dialog */}
      <SearchSheet open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default TopBar;
